import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import React from 'react';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'quaternary'
  | 'ghost';
export type ButtonSize = 'medium' | 'small' | 'icon' | 'iconSmall';

export type ButtonProps = {
  variant?: ButtonVariant;
  active?: boolean;
  activeClassname?: string;
  buttonSize?: ButtonSize;
} & Omit<MuiButtonProps, 'variant' | 'size'>;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      active,
      buttonSize = 'medium',
      children,
      disabled,
      variant = 'primary',
      sx,
      activeClassname,
      ...rest
    },
    ref
  ) => {
    // Map our custom variants to MUI variants
    const getMuiVariant = (): MuiButtonProps['variant'] => {
      if (variant === 'primary') return 'contained';
      if (variant === 'secondary') return 'outlined';
      if (variant === 'ghost') return 'text';
      return 'contained';
    };

    // Map our custom sizes to MUI sizes
    const getMuiSize = (): MuiButtonProps['size'] => {
      if (buttonSize === 'small' || buttonSize === 'iconSmall') return 'small';
      return 'medium';
    };

    // Handle icon button dimensions
    const getIconStyles = () => {
      if (buttonSize === 'icon') {
        return {
          minWidth: '32px',
          width: '32px',
          height: '32px',
          padding: '0px',
        };
      }
      if (buttonSize === 'iconSmall') {
        return {
          minWidth: '24px',
          width: '24px',
          height: '24px',
          padding: '0px',
        };
      }
      return {};
    };

    // Get color for ghost variant
    const getColor = (): MuiButtonProps['color'] => {
      if (variant === 'ghost') return 'inherit';
      return 'primary';
    };

    return (
      <MuiButton
        ref={ref}
        disabled={disabled}
        variant={getMuiVariant()}
        size={getMuiSize()}
        color={getColor()}
        sx={{
          borderRadius: 1,
          textTransform: 'none',
          fontWeight: 600,
          ...getIconStyles(),
          ...(active && variant === 'ghost'
            ? {
                backgroundColor: 'action.selected',
                '&:hover': {
                  backgroundColor: 'action.selected',
                },
              }
            : {}),
          ...(sx || {}),
        }}
        {...rest}
      >
        {children}
      </MuiButton>
    );
  }
);

Button.displayName = 'Button';
